import type {
  Ticket,
  CreateTicketRequest,
  AddMessageToTicketRequest,
  UpdateTicketRequest,
} from 'services/types/ticket';

import baseApi from './baseApi';

export const ticketsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getTickets: build.query<
      { tickets: Ticket[]; total: number },
      { includeAll?: boolean; page?: number; pageSize?: number; excludeClosed?: boolean }
    >({
      query: (params) => ({
        url: `/tickets?${new URLSearchParams({
          ...(params.includeAll ? { includeAll: 'true' } : {}),
          ...(params.page ? { page: params.page.toString() } : {}),
          ...(params.pageSize ? { pageSize: params.pageSize.toString() } : {}),
          ...(params.excludeClosed ? { excludeClosed: 'true' } : {}),
        }).toString()}`,
        method: 'GET',
      }),
    }),
    getTicket: build.query<Ticket, number>({
      query: (ticketId) => ({
        url: `/tickets/${ticketId}`,
        method: 'GET',
      }),
    }),
    createTicket: build.mutation<Ticket, CreateTicketRequest>({
      query: (ticketData) => ({
        url: '/tickets',
        method: 'POST',
        body: ticketData,
      }),
    }),
    addMessageToTicket: build.mutation<Ticket, { ticketId: number; data: AddMessageToTicketRequest }>({
      query: ({ ticketId, data }) => ({
        url: `/tickets/${ticketId}/messages`,
        method: 'POST',
        body: data,
      }),
    }),
    uploadFileToTicket: build.mutation<
      { message: string; url: string; fileName: string },
      { ticketId: number; file: File; showToUser?: boolean }
    >({
      query: ({ ticketId, file, showToUser }) => {
        const formData = new FormData();
        formData.append('file', file);
        if (showToUser !== undefined) {
          formData.append('showToUser', showToUser.toString());
        }

        return {
          url: `/tickets/${ticketId}/upload`,
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: (response: { message: string; url: string; fileName: string }) => response,
    }),
    analyzeTicket: build.mutation<Ticket, number>({
      query: (ticketId) => ({
        url: `/tickets/${ticketId}/analyze`,
        method: 'POST',
      }),
    }),
    getTicketCost: build.query<{ cost: number }, number>({
      query: (ticketId) => ({
        url: `/tickets/${ticketId}/cost`,
        method: 'GET',
      }),
    }),
    updateTicket: build.mutation<Ticket, { ticketId: number; data: UpdateTicketRequest }>({
      query: ({ ticketId, data }) => ({
        url: `/tickets/${ticketId}`,
        method: 'PATCH',
        body: data,
      }),
    }),
    transitionTicket: build.mutation<Ticket, { ticketId: number; data: { event: string } }>({
      query: ({ ticketId, data }) => ({
        url: `/tickets/${ticketId}/transition`,
        method: 'POST',
        body: data,
      }),
    }),
  }),
});

export const {
  useGetTicketsQuery,
  useGetTicketQuery,
  useCreateTicketMutation,
  useAddMessageToTicketMutation,
  useUploadFileToTicketMutation,
  useAnalyzeTicketMutation,
  useGetTicketCostQuery,
  useUpdateTicketMutation,
  useTransitionTicketMutation,
} = ticketsApi;
